import type { FloorAlarm, FloorAlarmState } from '@nodus/floorplan';
import type { FloorPlanAlarmRegion, FloorPlanDetail } from '@/lib/api/floorplans';

/** `floor_alarm.changed` on the staff socket (`core.consumers.RealTimeConsumer`). */
export const FLOOR_ALARM_WS_TYPE = 'floor_alarm.changed';

export type AlarmLocationType = 'floor' | 'address' | 'room';

/** One drawn region's new alarm state (`iot.floor_alarm.admin_payload`). */
export interface FloorAlarmMessage {
  /** Which region: a floor, an address (its floors' regions) or a room. Missing on old pushes = floor. */
  location_type?: AlarmLocationType;
  location_id?: number;
  /** Set for a floor only. */
  floor_id: number | null;
  location_name?: string;
  floor_name?: string;
  address_name?: string;
  state: FloorAlarmState | null;
  since: string | null;
  /** Pre-alarm: minutes until the alarm arms ("in about N minutes"), set per
   *  pre-alarm source; null when not set ("soon"). */
  warning_minutes?: number | null;
  /** Per-location order of the pushes; an older one is ignored. */
  seq?: number | null;
  changed_at?: string | null;
}

/** A parsed message always names its location. */
export type LocatedFloorAlarmMessage = FloorAlarmMessage & { location_type: AlarmLocationType; location_id: number };

const TYPES: ReadonlySet<string> = new Set(['floor', 'address', 'room']);

/** Parse a raw socket frame; null when it is not a floor-plan alarm change. */
export function floorAlarmMessage(raw: unknown): LocatedFloorAlarmMessage | null {
  const msg = raw as { type?: string; data?: FloorAlarmMessage } | null;
  if (msg?.type !== FLOOR_ALARM_WS_TYPE || !msg.data) return null;
  const data = msg.data;
  const type = data.location_type ?? 'floor';
  const id = data.location_type ? data.location_id : data.floor_id;
  if (!TYPES.has(type) || typeof id !== 'number') return null;
  return { ...data, location_type: type, location_id: id };
}

function locationKey(msg: Pick<LocatedFloorAlarmMessage, 'location_type' | 'location_id'>): string {
  return `${msg.location_type}:${msg.location_id}`;
}

/** Remembers the last push applied per location, so a late, older one (lower
 *  `seq`) is dropped. The admin plan has no polling to correct it later. */
export function createFloorAlarmOrder() {
  const last = new Map<string, number>();
  return (msg: LocatedFloorAlarmMessage): boolean => {
    if (msg.seq == null) return true;
    const k = locationKey(msg);
    const prev = last.get(k);
    if (prev != null && msg.seq <= prev) return false;
    last.set(k, msg.seq);
    return true;
  };
}

type PlanLike = Pick<FloorPlanDetail, 'floors'> & {
  rooms?: Array<{ id: number }>;
  alarm_regions?: FloorPlanAlarmRegion[];
};

function drawsLocation(plan: PlanLike, msg: LocatedFloorAlarmMessage): boolean {
  switch (msg.location_type) {
    case 'floor':
      return (plan.floors ?? []).some((f) => f.floor_id === msg.location_id);
    case 'address':
      return (plan.floors ?? []).some((f) => f.address_id === msg.location_id);
    case 'room':
      return (plan.rooms ?? []).some((r) => r.id === msg.location_id);
  }
}

/** The cached floor plan with one region's alarm replaced (removed when the
 *  location has no alarm links any more); unchanged when the plan doesn't draw it. */
export function withLocationAlarm<T extends PlanLike>(floorplan: T | undefined, msg: LocatedFloorAlarmMessage): T | undefined {
  if (!floorplan || !drawsLocation(floorplan, msg)) return floorplan;
  const same = (r: FloorPlanAlarmRegion) => r.location_type === msg.location_type && r.location_id === msg.location_id;
  const regions = (floorplan.alarm_regions ?? []).filter((r) => !same(r));
  if (msg.state) {
    regions.push({
      location_type: msg.location_type,
      location_id: msg.location_id,
      state: msg.state,
      since: msg.since,
      warning_minutes: msg.warning_minutes ?? null,
    });
  }
  let floors = floorplan.floors;
  if (msg.location_type === 'floor') {
    const alarm: FloorAlarm | null = msg.state ? { state: msg.state, since: msg.since } : null;
    floors = floorplan.floors.map((f) => (f.floor_id === msg.location_id ? { ...f, alarm } : f));
  }
  return { ...floorplan, floors, alarm_regions: regions };
}

/** The pre-alarm toast's lifetime when the minutes are not set; it goes anyway when the location leaves pre-alarm. */
const FALLBACK_TOAST_MINUTES = 15;

export function preAlarmToastText(msg: FloorAlarmMessage): string {
  const isFloor = (msg.location_type ?? 'floor') === 'floor';
  const name = isFloor ? msg.floor_name : msg.location_name;
  const context = [msg.location_type === 'room' ? msg.floor_name : '', msg.address_name]
    .filter((part) => part && part !== name)
    .join(', ');
  const where = context ? `${name} (${context})` : name;
  const minutes = msg.warning_minutes;
  return minutes && minutes > 0
    ? `The alarm on ${where} turns on in about ${minutes} minutes.`
    : `The alarm on ${where} turns on soon.`;
}

/** When the pre-alarm warning started, "2026-10-08 21:45:00" (the sv-SE
 *  locale's ISO-like date and time); undefined when the push carries none. */
export function preAlarmToastTimestamp(msg: FloorAlarmMessage): string | undefined {
  const iso = msg.since ?? msg.changed_at;
  if (!iso) return undefined;
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? undefined : at.toLocaleString('sv-SE');
}

export function preAlarmToastDuration(msg: FloorAlarmMessage): number {
  const minutes = msg.warning_minutes && msg.warning_minutes > 0 ? msg.warning_minutes : FALLBACK_TOAST_MINUTES;
  return minutes * 60 * 1000;
}

/** The toast id of a location: one toast per region. */
export function preAlarmToastId(msg: LocatedFloorAlarmMessage): string {
  return msg.location_type === 'floor' ? `floor-alarm-${msg.location_id}` : `floor-alarm-${msg.location_type}-${msg.location_id}`;
}
